import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';

/**
 * Leads Agri drone outputs (S3 `Leads+Agri_Drone+Outputs/`) shown under Large-scale → Drone Data.
 *
 * Arrangement: Province → Site → Flight → assets (NDVI / Ortho / DEM).
 *
 * This is a data registry: adding a site, flight or asset only needs an entry here — no changes to
 * MapLayerManager / MapView / LayerTogglePanel. (Isabela PhilRice and Cavite Silang keep their
 * original dedicated layer keys and are not part of this registry.)
 *
 * All COGs are served through TiTiler (`/api/tiles/WebMercatorQuad/...`):
 *  - NDVI → 3-band uint8 RGB (`bidx=1,2,3`)
 *  - Ortho → RGB from bands 1–3 (some flights are 7-band multispectral uint16; TiTiler auto-scales)
 *  - DEM → single-band float32 elevation drawn with a colormap over `demRange` (metres)
 */

export type DroneAssetKind = 'ndvi' | 'ortho' | 'dem';

export const DRONE_ASSET_KINDS: readonly DroneAssetKind[] = ['ndvi', 'ortho', 'dem'] as const;

export interface DroneFlight {
  id: string;
  /** Shown as a sub-heading when a site has more than one flight. */
  label: string;
  /** Survey date (ISO `YYYY-MM-DD`) — shown in the UI. */
  date?: string;
  /** Footprint `[minLon, minLat, maxLon, maxLat]` (EPSG:4326) from the COG bounds. */
  bbox: [number, number, number, number];
  /** Plain file names (`folder/file`) resolved with {@link droneAssetUrl}. */
  assets: Partial<Record<DroneAssetKind, string>>;
  /** DEM colormap range in metres (2nd–98th percentile of the elevation raster). */
  demRange?: [number, number];
}

export interface DroneSite {
  id: string;
  province: string;
  label: string;
  /** One-line description under the site header. */
  description?: string;
  flights: DroneFlight[];
}

const S3_BASE = 'https://digisaka.s3.amazonaws.com/Leads+Agri_Drone+Outputs/';

/** Build the S3 URL from a plain `folder/file` path (spaces become `+`, like the bucket links). */
export function droneAssetUrl(path: string): string {
  return (
    S3_BASE +
    path
      .split('/')
      .map((segment) => segment.replace(/ /g, '+'))
      .join('/')
  );
}

export const DRONE_SITES: DroneSite[] = [
  // ── Nueva Ecija ────────────────────────────────────────────────────────────
  {
    id: 'nuevaEcijaQuezon',
    province: 'Nueva Ecija',
    label: 'Quezon',
    flights: [
      {
        id: '20260306',
        label: 'Quezon production',
        date: '2026-03-06',
        bbox: [120.8100583196, 15.598242334550001, 120.82046766803, 15.60472059055],
        assets: {
          ndvi: 'Nueva Ecija_Quezon/Quezon production20hajsm 20260306_NDVI.tif',
          ortho: 'Nueva Ecija_Quezon/Quezon production20hajsm 20260306_Ortho.tif',
        },
      },
    ],
  },
  {
    id: 'nuevaEcijaLlanera',
    province: 'Nueva Ecija',
    label: 'Llanera',
    description: 'Four Gregorio plots.',
    flights: [
      {
        id: 'gregorio1',
        label: 'Gregorio 1',
        bbox: [120.97443930364801, 15.698373840738, 120.97813022563201, 15.700846559544],
        assets: {
          ndvi: 'Nueva Ecija_Llanera/Llanera Gregorio 1_NDVI.tif',
          ortho: 'Nueva Ecija_Llanera/Llanera Gregorio 1_Ortho.tif',
        },
      },
      {
        id: 'gregorio2',
        label: 'Gregorio 2',
        bbox: [120.974799827152, 15.696532421621999, 120.978491293464, 15.699003968882],
        assets: {
          ndvi: 'Nueva Ecija_Llanera/Llanera Gregorio 2_NDVI.tif',
          ortho: 'Nueva Ecija_Llanera/Llanera Gregorio 2_Ortho.tif',
        },
      },
      {
        id: 'gregorio3',
        label: 'Gregorio 3',
        bbox: [120.97428837396, 15.695865128385002, 120.97916527666199, 15.698659362282001],
        assets: {
          ndvi: 'Nueva Ecija_Llanera/Llanera Gregorio 3_NDVI.tif',
          ortho: 'Nueva Ecija_Llanera/Llanera Gregorio 3_Ortho.tif',
        },
      },
      {
        id: 'gregorio4',
        label: 'Gregorio 4',
        bbox: [120.97525614662, 15.693638504692998, 120.979403929214, 15.696455736268998],
        assets: {
          ndvi: 'Nueva Ecija_Llanera/Llanera Gregorio 4_NDVI.tif',
          ortho: 'Nueva Ecija_Llanera/Llanera Gregorio 4_Ortho.tif',
        },
      },
    ],
  },
  {
    id: 'nuevaEcijaGuimba',
    province: 'Nueva Ecija',
    label: 'Guimba',
    flights: [
      {
        id: '20260306',
        label: 'RND Guimba',
        date: '2026-03-06',
        bbox: [120.762495642124, 15.638318539021, 120.76759469722799, 15.643004176336],
        assets: {
          ndvi: 'Nueva Ecija_Guimba/RNDGuimba 20260306_NDVI.tif',
          ortho: 'Nueva Ecija_Guimba/RNDGuimba 20260306_Ortho.tif',
        },
      },
    ],
  },

  // ── Batangas ───────────────────────────────────────────────────────────────
  {
    id: 'batangasSanJuan',
    province: 'Batangas',
    label: 'San Juan',
    flights: [
      {
        id: '20251105',
        label: 'San Juan',
        date: '2025-11-05',
        bbox: [121.334218634717, 13.839483926245, 121.33796609487601, 13.844342678625],
        assets: {
          ndvi: 'Batangas_San Juan/San Juan 20251105_NDVI.tif',
          ortho: 'Batangas_San Juan/San Juan 20251105_Ortho.tif',
        },
      },
    ],
  },

  // ── Laguna ─────────────────────────────────────────────────────────────────
  {
    id: 'lagunaStaMaria',
    province: 'Laguna',
    label: 'Sta. Maria',
    flights: [
      {
        id: '20250522',
        label: 'Yolanda',
        date: '2025-05-22',
        bbox: [121.41459926114399, 14.49747062286, 121.41957256495199, 14.502158501091],
        demRange: [68, 87],
        assets: {
          ndvi: 'Laguna_Sta Maria/Sta Maria_Yolanda_20250522_NDVI.tif',
          ortho: 'Laguna_Sta Maria/Sta Maria_Yolanda_20250522_Ortho.tif',
          dem: 'Laguna_Sta Maria/Sta Maria_Yolanda_20250522_DEM.tif',
        },
      },
    ],
  },
  {
    id: 'lagunaLosBanos',
    province: 'Laguna',
    label: 'Los Baños',
    flights: [
      {
        id: '20251217',
        label: 'DA-BPI drone demo',
        date: '2025-12-17',
        bbox: [121.222576743768, 14.174796915707999, 121.224481532964, 14.177184229494],
        assets: {
          ndvi: 'Laguna_Los Banos/DA BPI LB Drone Demo 12172025_NDVI.tif',
          ortho: 'Laguna_Los Banos/DA BPI LB Drone Demo 12172025_Ortho.tif',
        },
      },
    ],
  },
  {
    id: 'lagunaCalauan',
    province: 'Laguna',
    label: 'Calauan',
    description: 'FARMEX left and right trials.',
    flights: [
      {
        id: 'leftTrial20261001',
        label: 'FARMEX Left Trial',
        date: '2026-10-01',
        bbox: [121.310507023993, 14.174267301360002, 121.314726364289, 14.177558442570001],
        assets: {
          ortho: 'Laguna_Calauan/FARMEX Left Trial 10012026_Ortho.tif',
        },
      },
      {
        id: 'rightTrial20261001',
        label: 'FARMEX Right Trial',
        date: '2026-10-01',
        bbox: [121.31258771710799, 14.174173638592, 121.31749269450098, 14.178260903088],
        assets: {
          ortho: 'Laguna_Calauan/FARMEX Right Trial 10012026_Ortho.tif',
        },
      },
      {
        id: 'rightTrial20260520',
        label: 'FARMEX Right Trial',
        date: '2026-05-20',
        bbox: [121.31341938442401, 14.175280091039, 121.31757291548001, 14.177765969703],
        assets: {
          ndvi: 'Laguna_Calauan/FARMEX Right Trial 05202026_NDVI.tif',
          ortho: 'Laguna_Calauan/FARMEX Right Trial 05202026_Ortho.tif',
        },
      },
    ],
  },
  {
    id: 'lagunaPila',
    province: 'Laguna',
    label: 'Pila',
    flights: [
      {
        id: '20250506',
        label: 'IA Pila',
        date: '2025-05-06',
        bbox: [121.35324169671999, 14.237980610078, 121.36513799535498, 14.248939259422],
        demRange: [49, 56],
        assets: {
          ortho: 'Laguna_Pila/IA Pila 20250506_Ortho.tif',
          dem: 'Laguna_Pila/IA Pila 20250506_DEM.tif',
        },
      },
    ],
  },

  // ── Quezon ─────────────────────────────────────────────────────────────────
  {
    id: 'quezonDolores',
    province: 'Quezon',
    label: 'Dolores',
    flights: [
      {
        id: '20260204',
        label: 'Dolores',
        date: '2026-02-04',
        bbox: [121.434262531988, 14.021136706456, 121.439950623733, 14.02844962167],
        demRange: [472, 557],
        assets: {
          ndvi: 'Quezon_Dolores/Dolores 02042026_NDVI.tif',
          ortho: 'Quezon_Dolores/Dolores 02042026_Ortho.tif',
          dem: 'Quezon_Dolores/Dolores 02042026_DEM.tif',
        },
      },
    ],
  },
];

/** Provinces in display order, each with its sites. */
export function getDroneSitesByProvince(): { province: string; sites: DroneSite[] }[] {
  const order: string[] = [];
  const byProvince = new Map<string, DroneSite[]>();
  for (const site of DRONE_SITES) {
    if (!byProvince.has(site.province)) {
      byProvince.set(site.province, []);
      order.push(site.province);
    }
    byProvince.get(site.province)!.push(site);
  }
  return order.map((province) => ({ province, sites: byProvince.get(province)! }));
}

// ── Header navigation (one entry per province) ──────────────────────────────

const PROVINCE_NAV_PREFIX = 'dronePv_';

/** Section id for a province in the large-scale header menu, e.g. `dronePv_NuevaEcija`. */
export function droneProvinceNavId(province: string): string {
  return PROVINCE_NAV_PREFIX + province.replace(/\s+/g, '');
}

export function isDroneProvinceNavId(sectionId: string): boolean {
  return sectionId.startsWith(PROVINCE_NAV_PREFIX);
}

/** Header-menu entries for the registry, in display order. */
export function getDroneProvinceNavEntries(): { id: string; province: string; label: string }[] {
  return getDroneSitesByProvince().map(({ province }) => ({
    id: droneProvinceNavId(province),
    province,
    label: `${province} drone`,
  }));
}

// ── Layer ids ───────────────────────────────────────────────────────────────

/** Unique id for one toggle, e.g. `lagunaCalauan:rightTrial20260520:ndvi`. */
export function droneLayerId(siteId: string, flightId: string, kind: DroneAssetKind): string {
  return `${siteId}:${flightId}:${kind}`;
}

export interface ResolvedDroneLayer {
  id: string;
  site: DroneSite;
  flight: DroneFlight;
  kind: DroneAssetKind;
  cogUrl: string;
}

let layerIndex: Map<string, ResolvedDroneLayer> | null = null;

function getLayerIndex(): Map<string, ResolvedDroneLayer> {
  if (layerIndex) return layerIndex;
  const index = new Map<string, ResolvedDroneLayer>();
  for (const site of DRONE_SITES) {
    for (const flight of site.flights) {
      for (const kind of DRONE_ASSET_KINDS) {
        const path = flight.assets[kind];
        if (!path) continue;
        const id = droneLayerId(site.id, flight.id, kind);
        index.set(id, { id, site, flight, kind, cogUrl: droneAssetUrl(path) });
      }
    }
  }
  layerIndex = index;
  return index;
}

export function findDroneLayer(layerId: string): ResolvedDroneLayer | null {
  return getLayerIndex().get(layerId) ?? null;
}

/** State + handlers MapView exposes so the Drone Data panel can drive registry layers. */
export interface DroneSitesControls {
  activeLayerId: string | null;
  opacity: Record<string, number>;
  onToggle: (layerId: string, visible: boolean) => void;
  onOpacityChange: (layerId: string, opacity: number) => void;
}

// ── TiTiler URLs ────────────────────────────────────────────────────────────

const PREVIEW_BASE = DIGISAKA_API_ORIGIN + '/api/preview';
const TITILER_TILE_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';

export const DRONE_DEM_NODATA = -32767;
export const DRONE_DEM_COLORMAP = 'terrain';

function rgbQuery(cogUrl: string): string {
  return `url=${encodeURIComponent(cogUrl)}&bidx=1&bidx=2&bidx=3&resampling=lanczos`;
}

function demQuery(layer: ResolvedDroneLayer): string {
  const [min, max] = layer.flight.demRange ?? [0, 100];
  return (
    `url=${encodeURIComponent(layer.cogUrl)}&bidx=1&rescale=${min},${max}` +
    `&colormap_name=${DRONE_DEM_COLORMAP}&nodata=${DRONE_DEM_NODATA}&resampling=bilinear`
  );
}

/** WebMercatorQuad XYZ template for a drone layer. */
export function buildDroneLayerTileUrl(layer: ResolvedDroneLayer): string {
  return `${TITILER_TILE_BASE}?${layer.kind === 'dem' ? demQuery(layer) : rgbQuery(layer.cogUrl)}`;
}

/** Layer-list thumbnail — same TiTiler params as the map tiles. */
export function buildDroneLayerPreviewUrl(layer: ResolvedDroneLayer, maxSize = 160): string {
  if (layer.kind === 'dem') {
    return `${PREVIEW_BASE}?${demQuery(layer)}&max_size=${maxSize}`;
  }
  return `${PREVIEW_BASE}?url=${encodeURIComponent(layer.cogUrl)}&max_size=${maxSize}&bidx=1&bidx=2&bidx=3`;
}

// ── Display helpers ─────────────────────────────────────────────────────────

export const DRONE_ASSET_LABEL: Record<DroneAssetKind, string> = {
  ndvi: 'NDVI',
  ortho: 'Orthomosaic',
  dem: 'Elevation (DEM)',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-03-06` → `6 Mar 2026`. */
export function formatDroneFlightDate(iso?: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** Layer ids that belong to a site (used to show the “active” dot on collapsed headers). */
export function droneSiteLayerIds(site: DroneSite): string[] {
  const ids: string[] = [];
  for (const flight of site.flights) {
    for (const kind of DRONE_ASSET_KINDS) {
      if (flight.assets[kind]) ids.push(droneLayerId(site.id, flight.id, kind));
    }
  }
  return ids;
}
